import { Button, Col, Collapse, Divider, Empty, Row, Space, Tooltip, Typography } from 'antd';
import { ShoppingCart } from 'lucide-react';
import { Activity, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ReactComponent as Ambient } from '../../../assets/svg/Ambient.svg';
import { ReactComponent as Chilled } from '../../../assets/svg/Chilled.svg';
import { ReactComponent as Fresh } from '../../../assets/svg/Fresh.svg';
import { ReactComponent as Frozen } from '../../../assets/svg/Frozen.svg';
import { ReactComponent as Hallal } from '../../../assets/svg/Hallal.svg';
import { ReactComponent as New } from '../../../assets/svg/New.svg';
import { ReactComponent as Organic } from '../../../assets/svg/Organic.svg';
import { ReactComponent as Popular } from '../../../assets/svg/Popular.svg';
import { ReactComponent as Vegan } from '../../../assets/svg/Vegan.svg';
import { ReactComponent as Vegetarian } from '../../../assets/svg/Vegetarian.svg';
import ProductCard from '../../../components/ui/ProductCard';
import ProductImagesSlider from '../../../components/ui/ProductImagesSlider';
import { VAT_RATE } from '../../../constants';
import { getProductDetails } from '../../../redux/actions/products';
import { calculateVAT, formatCurrency, getProductImageUrl } from '../../../utils';
import QuantityInput from './../../../components/ui/QuantityInput/index';
import { useSelector } from 'react-redux';
import _ from 'lodash';

const { Title, Text, Paragraph } = Typography;

const panelStyle = {
    marginBottom: 16,
    background: 'white',
    overflow: 'hidden',
    borderRadius: '0px',
    border: '1px solid #f0f0f0',
    boxShadow: '0 2px 2px rgba(0, 0, 0, 0.15)',
};

const CatalogueDetails = () => {
    const { productId } = useParams();
    const { singleProduct: product, products } = useSelector((state) => state.productsSlice);
    const [quantity, setQuantity] = useState(1);

    const similarProducts = (products || [])?.slice(0, 6);
    let images = [];

    if (product?.profile_image || product?.gallery_images) {
        const featuredImageUrl = getProductImageUrl(product);

        images = [featuredImageUrl];

        const gallery = _.attempt(() => {
            if (_.isArray(product.gallery_images)) return product.gallery_images;
            if (_.isString(product.gallery_images))
                return JSON.parse(product.gallery_images);
            return [];
        });

        const galleryList = _.isError(gallery) ? [] : gallery;

        const galleryUrls = _(galleryList)
            ?.map((img) => getProductImageUrl({ ...product, profile_image: img }))
            ?.compact()
            ?.uniq()
            ?.without(featuredImageUrl)
            ?.value();

        images = _.concat(images, galleryUrls);
    }

    const isTaxable = product?.taxable?.toLowerCase() === 'taxable';

    const totalPrice = isTaxable
        ? calculateVAT({ unitPrice: product?.unit_price, quantity, vatField: 20 })?.totalWithVAT
        : product?.unit_price * quantity;

    useEffect(() => {
        if (productId) getProductDetails({ id: productId });
    }, [productId]);

    return (
        <div className="product-page my-0 mx-auto">
            <div className="product-page-container">
                <Row gutter={[48, 32]} className="product-main p-0 mb-6">
                    <Col xs={24} md={12} lg={12}>
                        <ProductImagesSlider images={images} />
                    </Col>

                    <Col xs={24} md={12} lg={12}>
                        <div className="product-info">
                            <div className="product-category inline-flex align-items-center gap-2 py-1 px-2 border-round-md shadow-1">
                                <Text className="metadata-label">Product:</Text>
                                <Text className="metadata-value">{product?.code}</Text>
                            </div>

                            <Title level={1} className="product-title font-bold mb-3 mt-0">
                                {product?.name}
                            </Title>

                            <div className="product-price p-4 border-round-md mb-3">
                                <div className="flex align-items-center justify-content-between flex-wrap-reverse lg:mb-2">
                                    <Text type="secondary" className="text-lg">
                                        Price per case
                                    </Text>
                                    <Activity mode={isTaxable ? 'visible' : 'hidden'}>
                                        <span className="vat-indicator py-1 px-2 font-semibold border-round-lg">
                                            Includes {VAT_RATE * 100}% VAT
                                        </span>
                                    </Activity>
                                </div>

                                <Title level={2} className="price-value m-0">
                                    {formatCurrency(product?.unit_price)}
                                </Title>
                            </div>

                            <Paragraph type="secondary" className="text-base line-height-3 mb-3">
                                {product?.description}
                            </Paragraph>

                            <div className="product-category inline-flex align-items-center flex-wrap gap-1 py-1 px-2 border-round-md shadow-1 mb-3">
                                <Text className="metadata-label">Category:</Text>
                                <Text className="metadata-value">{product?.sub_category}</Text>
                            </div>

                            <Space className="product-type w-full overflow-x-auto mb-3">
                                <Tooltip title="Hallal">
                                    <Hallal />
                                </Tooltip>
                                <Tooltip title="Vegetarian">
                                    <Vegetarian />
                                </Tooltip>
                                <Tooltip title="Vegan">
                                    <Vegan />
                                </Tooltip>
                                <Tooltip title="Frozen">
                                    <Frozen />
                                </Tooltip>
                                <Tooltip title="Chilled">
                                    <Chilled />
                                </Tooltip>
                                <Tooltip title="Ambient">
                                    <Ambient />
                                </Tooltip>
                                <Tooltip title="Organic">
                                    <Organic />
                                </Tooltip>
                                <Tooltip title="Popular">
                                    <Popular />
                                </Tooltip>
                                <Tooltip title="New">
                                    <New />
                                </Tooltip>
                                <Tooltip title="Fresh">
                                    <Fresh />
                                </Tooltip>
                            </Space>

                            <div className="product-actions flex align-items-end flex-wrap gap-3 mb-3">
                                <div className="quantity-selector flex flex-column gap-2">
                                    <Text strong className="quantity-label">
                                        Quantity (cases)
                                    </Text>
                                    <QuantityInput value={quantity} onChange={setQuantity} />
                                </div>

                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ShoppingCart size={20} />}
                                    className="add-to-cart-btn flex-1"
                                >
                                    Add to Cart
                                </Button>
                            </div>

                            <div className="total-price">
                                <Text type="secondary">Total: </Text>
                                <Text strong className="total-amount">
                                    {formatCurrency(totalPrice)}
                                </Text>
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="similar-products-section mb-4">
                    <Title level={3} className="text-center font-semibold mb-4">
                        Similar Products
                    </Title>

                    <Row justify="center" gutter={[8, 8]} className="similar-products-grid">
                        {similarProducts && !!similarProducts?.length ? (
                            similarProducts.slice(0, 6).map((similarProduct) => (
                                <Col xs={12} sm={8} md={8} lg={4} key={similarProduct?.netsuite_id}>
                                    <ProductCard product={similarProduct} />
                                </Col>
                            ))
                        ) : (
                            <Col span={24} className="text-center">
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <Text type="secondary">
                                            No similar products available.
                                        </Text>
                                    }
                                />
                            </Col>
                        )}
                    </Row>
                </div>

                <Divider />

                <div className="product-details-section lg:mb-4">
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Collapse
                                size="large"
                                bordered={false}
                                expandIconPosition="end"
                                style={{
                                    background: 'transparent',
                                }}
                                items={[
                                    { key: '1', label: 'Description', style: panelStyle },
                                    { key: '2', label: 'Ingredients & Allergens', style: panelStyle },
                                    { key: '3', label: 'Nutrition', style: panelStyle },
                                ]}
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <Collapse
                                size="large"
                                bordered={false}
                                expandIconPosition="end"
                                style={{
                                    background: 'transparent',
                                }}
                                items={[
                                    { key: '1', label: 'Reviews', style: panelStyle },
                                    { key: '2', label: 'Questions & Answers', style: panelStyle },
                                    { key: '3', label: 'Disclaimer', style: panelStyle },
                                ]}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default CatalogueDetails;
